
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { HardDrive, Check, Settings, Info, Database, Smartphone } from 'lucide-react';
import { universalStorage } from '@/services/storage/universalStorage';
import { toast } from 'sonner';

export const StorageManagement = () => {
  const [storageInfo, setStorageInfo] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    loadStorageInfo();
  }, []);

  const loadStorageInfo = async () => {
    try {
      const info = await universalStorage.getStorageInfo();
      setStorageInfo(info);
    } catch (error) {
      console.error('Error loading storage info:', error);
    }
  };

  const handleRefreshStorage = async () => {
    setIsLoading(true);
    try {
      universalStorage.clearCache();
      await loadStorageInfo();
      toast.success('Storage data refreshed successfully');
    } catch (error) {
      toast.error('Failed to refresh storage data');
    } finally {
      setIsLoading(false);
    }
  };

  if (!storageInfo) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center p-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <HardDrive className="h-5 w-5" />
            Storage System
          </CardTitle>
          <CardDescription>
            Your app uses localStorage with enhanced reliability features for permanent data storage
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between p-4 bg-green-50 dark:bg-green-950/20 rounded-lg border border-green-200 dark:border-green-800">
            <div className="flex items-center gap-3">
              <Check className="h-5 w-5 text-green-600" />
              <div>
                <p className="font-medium text-green-800 dark:text-green-200">
                  localStorage Active
                </p>
                <p className="text-sm text-green-600 dark:text-green-400">
                  Data persists permanently with automatic backups
                </p>
              </div>
            </div>
            <Badge variant="secondary" className="bg-green-100 text-green-800">
              Enhanced
            </Badge>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center p-3 bg-muted rounded-lg">
              <p className="text-2xl font-bold text-primary">{storageInfo.keysCount}</p>
              <p className="text-sm text-muted-foreground">Data Keys</p>
            </div>
            <div className="text-center p-3 bg-muted rounded-lg">
              <p className="text-2xl font-bold text-primary">{storageInfo.totalSize}</p>
              <p className="text-sm text-muted-foreground">Storage Used</p>
            </div>
            <div className="text-center p-3 bg-muted rounded-lg">
              <p className="text-2xl font-bold text-primary">{storageInfo.cacheSize}</p>
              <p className="text-sm text-muted-foreground">Cache Items</p>
            </div>
            <div className="text-center p-3 bg-muted rounded-lg">
              <p className="text-2xl font-bold text-primary">{storageInfo.backupCount}</p>
              <p className="text-sm text-muted-foreground">Backups</p>
            </div>
          </div>

          <div className="flex gap-2">
            <Button 
              onClick={handleRefreshStorage} 
              disabled={isLoading}
              className="flex items-center gap-2"
            >
              <Database className="h-4 w-4" />
              {isLoading ? 'Refreshing...' : 'Refresh Storage'}
            </Button>
          </div>

          <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-950/20 rounded-lg border border-blue-200 dark:border-blue-800">
            <h4 className="font-medium text-blue-800 dark:text-blue-200 mb-2">
              🛡️ Enhanced localStorage Features
            </h4>
            <ul className="text-sm text-blue-700 dark:text-blue-300 space-y-1">
              <li>• Automatic emergency backups every 2 minutes</li>
              <li>• Data integrity validation and recovery</li>
              <li>• Permanent storage across browser sessions</li>
              <li>• Intelligent cache management</li>
              <li>• Last backup: {storageInfo.lastBackup}</li>
            </ul>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
